'use strict';

/**
 * Video Hero Banner - Vimeo and HTML5 Video Support
 */

var initAttempts = 0;
var maxAttempts = 100;
var initStarted = false;
var playerInstances = new Map();

// Shared helper functions
var helpers = {
    formatTime: function (seconds) {
        var h = Math.floor(seconds / 3600);
        var m = Math.floor((seconds % 3600) / 60);
        var s = Math.floor(seconds % 60);
        return h > 0 ? h + ':' + (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s : m + ':' + (s < 10 ? '0' : '') + s;
    },

    hideElement: function (el) {
        if (el) {
            el.style.cssText = 'opacity: 0; pointer-events: none; display: none;';
        }
    },

    showElement: function (el, display) {
        if (el) {
            el.style.cssText = 'opacity: 1; pointer-events: auto; display: ' + (display || 'block') + ';';
        }
    },

    getVideoId: function (mobile, tablet, desktop) {
        return window.innerWidth < 768 ? (mobile || desktop) : (desktop || mobile);
    },

    addClickAndKeyboard: function (el, handler) {
        if (!el) return;
        el.addEventListener('click', handler);
        el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handler();
            }
        });
    }
};

// HTML5 video handler
/**
 * Initialize HTML5 video player
 * @param {HTMLElement} container - Container element
 * @param {HTMLVideoElement} video - Video element
 */
function initializeHTML5Video(container, video) {
    var autoplay = container.getAttribute('data-autoplay') === 'true';
    var els = {
        playBtn: container.querySelector('.video-hero-banner-play-btn'),
        poster: container.querySelector('.video-hero-banner-poster'),
        overlay: container.querySelector('.video-hero-banner-overlay'),
        content: container.querySelector('.video-hero-banner-content'),
        controls: container.querySelector('.video-hero-banner-controls'),
        clickOverlay: container.querySelector('.video-hero-banner-click-overlay')
    };

    // Ensure poster is visible on page load (not in autoplay mode)
    if (els.poster && !autoplay) {
        els.poster.style.cssText = 'opacity: 1; pointer-events: auto; display: block;';
    }

    if (autoplay) {
        ['playBtn', 'poster', 'controls'].forEach(function (key) {
            if (els[key]) els[key].style.display = 'none';
        });
        return;
    }

    /**
     * Play video and update UI
     */
    function playVideo() {
        video.muted = false;
        video.play().then(function () {
            ['playBtn', 'poster', 'overlay', 'content'].forEach(function (key) {
                helpers.hideElement(els[key]);
            });
            // Add video-started class to enable controls
            container.classList.add('video-started');
            // Controls visibility now handled by CSS hover
            if (els.controls) els.controls.style.display = 'block';
            helpers.showElement(els.clickOverlay);
            container.classList.add('video-playing');
        }).catch(function () {});
    }

    /**
     * Toggle play/pause state
     */
    function togglePlayPause() {
        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    }

    // Play button click and keyboard support
    if (els.playBtn) {
        els.playBtn.addEventListener('click', playVideo);

        // Keyboard support (Enter and Space keys)
        els.playBtn.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                playVideo();
            }
        });
    }

    // Click overlay for play/pause (mobile)
    if (els.clickOverlay) {
        helpers.addClickAndKeyboard(els.clickOverlay, togglePlayPause);
    }
}

// Vimeo video handler
/**
 * Initialize Vimeo video player
 * @param {HTMLElement} container - Container element
 */
function initializeVimeoVideo(container) {
    var playerEl = container.querySelector('.video-hero-banner-player');
    if (!playerEl || container.getAttribute('data-vimeo-initialized') === 'true') return;

    var videoId = helpers.getVideoId(
        container.getAttribute('data-vimeo-id-mobile'),
        container.getAttribute('data-vimeo-id-tablet'),
        container.getAttribute('data-vimeo-id-desktop')
    );
    if (!videoId) return;

    var autoplay = container.getAttribute('data-autoplay') === 'true';
    var loop = container.getAttribute('data-loop') === 'true';

    // Create fresh player element
    var freshDiv = document.createElement('div');
    freshDiv.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%';
    playerEl.innerHTML = '';
    playerEl.appendChild(freshDiv);

    // Cache all elements
    var els = {
        playBtn: container.querySelector('.video-hero-banner-play-btn'),
        pauseBtn: container.querySelector('.video-hero-banner-pause-btn'),
        clickOverlay: container.querySelector('.video-hero-banner-click-overlay'),
        poster: container.querySelector('.video-hero-banner-poster'),
        videoOverlay: container.querySelector('.video-hero-banner-overlay'),
        content: container.querySelector('.video-hero-banner-content'),
        controls: container.querySelector('.video-hero-banner-controls'),
        playIcon: container.querySelector('.play-icon'),
        pauseIcon: container.querySelector('.pause-icon'),
        timeCurrent: container.querySelector('.video-time-current'),
        timeDuration: container.querySelector('.video-time-duration'),
        progressBar: container.querySelector('.video-control-progress-bar'),
        progressFilled: container.querySelector('.video-control-progress-filled'),
        progressHandle: container.querySelector('.video-control-progress-handle'),
        volumeIcon: container.querySelector('.volume-icon'),
        muteIcon: container.querySelector('.mute-icon'),
        volumeBar: container.querySelector('.video-control-volume-bar'),
        volumeFilled: container.querySelector('.video-control-volume-filled'),
        fullscreenIcon: container.querySelector('.fullscreen-icon'),
        fullscreenExitIcon: container.querySelector('.fullscreen-exit-icon')
    };

    var playerKey = 'player-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    var userInitiatedPlay = false;

    /**
     * Get player instance
     * @returns {Object|null} Player instance or null
     */
    function getPlayer() {
        return playerInstances.get(playerKey);
    }

    /**
     * Toggle play/pause state
     */
    function togglePlayPause() {
        var p = getPlayer();
        if (!p) return;
        p.getPaused().then(function (paused) {
            if (paused) {
                userInitiatedPlay = true;
                p.play();
            } else {
                p.pause();
            }
        });
    }

    /**
     * Handle video ended event
     */
    function handleVideoEnded() {
        var p = getPlayer();
        if (autoplay) {
            if (p) p.setCurrentTime(0).then(function () { return p.play(); }).catch(function () {});
            return;
        }

        ['pauseBtn', 'playBtn'].forEach(function (k) { if (els[k]) els[k].style.display = 'none'; });
        if (els.playIcon) els.playIcon.style.display = 'block';
        if (els.pauseIcon) els.pauseIcon.style.display = 'none';
        helpers.showElement(els.clickOverlay);
        // Controls visibility now handled by CSS hover
        if (els.controls) els.controls.style.display = 'block';
        if (p) {
            p.setCurrentTime(0).catch(function () {});
            p.pause().catch(function () {});
        }
    }

    try {
        var player = new window.Vimeo.Player(freshDiv, {
            id: videoId,
            autoplay: true,
            loop: loop,
            controls: false,
            muted: true,
            background: false,
            responsive: true,
            byline: false,
            title: false,
            portrait: false,
            pip: false,
            autopause: false,
            playsinline: true,
            transparent: false
        });

        playerInstances.set(playerKey, player);
        container.setAttribute('data-player-key', playerKey);

        player.ready().then(function () {
            container.setAttribute('data-vimeo-initialized', 'true');

            // Fix Vimeo wrapper positioning
            var iframe = playerEl.querySelector('iframe');
            if (iframe && iframe.parentElement) {
                iframe.parentElement.style.setProperty('position', 'static', 'important');
            }

            // Setup autoplay vs manual play
            if (autoplay) {
                ['playBtn', 'pauseBtn'].forEach(function (k) {
                    if (els[k]) {
                        els[k].style.display = 'none';
                        els[k].style.pointerEvents = 'none';
                    }
                });
                if (els.clickOverlay) {
                    els.clickOverlay.style.pointerEvents = 'none';
                    els.clickOverlay.style.display = 'none';
                }
                if (els.controls) els.controls.style.display = 'none';
            } else {
                player.pause().then(function () { return player.setCurrentTime(0); }).catch(function () {});
            }

            // Initialize duration
            player.getDuration().then(function (d) {
                if (els.timeDuration) els.timeDuration.textContent = helpers.formatTime(d);
            });

            // Initialize volume/mute icons
            player.getMuted().then(function (muted) {
                if (els.volumeIcon) els.volumeIcon.style.display = muted ? 'none' : 'block';
                if (els.muteIcon) els.muteIcon.style.display = muted ? 'block' : 'none';
            });

            player.getVolume().then(function (volume) {
                if (els.volumeFilled) els.volumeFilled.style.width = (volume * 100) + '%';
            });

            // Main play button
            if (els.playBtn) {
                helpers.addClickAndKeyboard(els.playBtn, function () {
                    var p = getPlayer();
                    if (!p) return;
                    userInitiatedPlay = true;
                    p.setMuted(false).then(function () { return p.play(); }).then(function () {
                        els.playBtn.style.display = 'none';
                        if (els.pauseBtn) els.pauseBtn.style.display = 'none';
                        // Controls visibility now handled by CSS hover
                        if (els.controls) els.controls.style.display = 'block';
                        ['videoOverlay', 'poster', 'content'].forEach(function (k) { helpers.hideElement(els[k]); });
                    }).catch(function () {});
                });
            }

            // Control bar play/pause
            var ctrlPlayPause = container.querySelector('.video-control-play-pause');
            if (ctrlPlayPause) ctrlPlayPause.addEventListener('click', togglePlayPause);

            // Progress bar seek
            if (els.progressBar) {
                els.progressBar.addEventListener('click', function (e) {
                    var p = getPlayer();
                    if (!p) return;
                    var rect = els.progressBar.getBoundingClientRect();
                    var percent = (e.clientX - rect.left) / rect.width;
                    p.getDuration().then(function (d) { p.setCurrentTime(d * percent); });
                });
            }

            // Volume controls - toggle mute/unmute
            var muteBtn = container.querySelector('.video-control-mute');
            if (muteBtn) {
                muteBtn.addEventListener('click', function () {
                    var p = getPlayer();
                    if (!p) return;
                    p.getMuted().then(function (muted) {
                        p.setMuted(!muted);
                    });
                });
            }

            // Volume bar slider (desktop only)
            if (els.volumeBar) {
                els.volumeBar.addEventListener('click', function (e) {
                    var p = getPlayer();
                    if (!p) return;
                    var rect = els.volumeBar.getBoundingClientRect();
                    var newVolume = (e.clientX - rect.left) / rect.width;
                    p.setVolume(newVolume).then(function () {
                        // Unmute if user is adjusting volume
                        if (newVolume > 0) p.setMuted(false);
                    });
                });
            }

            // Fullscreen
            var fsBtn = container.querySelector('.video-control-fullscreen');
            if (fsBtn) {
                fsBtn.addEventListener('click', function () {
                    var p = getPlayer();
                    if (!p) return;
                    p.getFullscreen().then(function (fs) {
                        if (fs) {
                            p.exitFullscreen();
                        } else {
                            p.requestFullscreen();
                        }
                    });
                });
            }

            // Click overlay
            if (els.clickOverlay) {
                helpers.addClickAndKeyboard(els.clickOverlay, togglePlayPause);
            }
        }).catch(function () {});

        // Player events
        player.on('play', function () {
            if (autoplay && !userInitiatedPlay) {
                if (els.poster) {
                    els.poster.style.transition = 'opacity 0.5s ease-in-out';
                    els.poster.style.opacity = '0';
                    setTimeout(function () { els.poster.style.display = 'none'; }, 500);
                }
            } else if (userInitiatedPlay) {
                container.classList.add('video-playing');
                if (els.playIcon) els.playIcon.style.display = 'none';
                if (els.pauseIcon) els.pauseIcon.style.display = 'block';
                // Controls visibility now handled by CSS hover
                if (els.controls) els.controls.style.display = 'block';
                helpers.showElement(els.clickOverlay);
                ['videoOverlay', 'poster', 'content', 'playBtn'].forEach(function (k) { helpers.hideElement(els[k]); });
                if (els.playBtn) els.playBtn.style.pointerEvents = 'none';
            }
        });

        player.on('pause', function () {
            if (els.playIcon) els.playIcon.style.display = 'block';
            if (els.pauseIcon) els.pauseIcon.style.display = 'none';
        });

        player.on('ended', handleVideoEnded);

        player.on('timeupdate', function (d) {
            var percent = (d.seconds / d.duration) * 100;
            if (els.progressFilled) els.progressFilled.style.width = percent + '%';
            if (els.progressHandle) els.progressHandle.style.left = percent + '%';
            if (els.timeCurrent) els.timeCurrent.textContent = helpers.formatTime(d.seconds);
        });

        player.on('volumechange', function (d) {
            if (els.volumeFilled) els.volumeFilled.style.width = (d.volume * 100) + '%';
            // Check both muted state and volume level
            var p = getPlayer();
            if (p) {
                p.getMuted().then(function (muted) {
                    var isMuted = muted || d.volume === 0;
                    if (els.volumeIcon) els.volumeIcon.style.display = isMuted ? 'none' : 'block';
                    if (els.muteIcon) els.muteIcon.style.display = isMuted ? 'block' : 'none';
                });
            }
        });

        player.on('fullscreenchange', function (d) {
            if (els.fullscreenIcon) els.fullscreenIcon.style.display = d.fullscreen ? 'none' : 'block';
            if (els.fullscreenExitIcon) els.fullscreenExitIcon.style.display = d.fullscreen ? 'block' : 'none';
        });

        // Prevent Vimeo end screen
        player.getDuration().then(function (duration) {
            player.on('timeupdate', function (d) {
                if (d.seconds >= duration - 0.5 && d.seconds < duration) {
                    var p = getPlayer();
                    if (p) {
                        p.pause().catch(function () {});
                        handleVideoEnded();
                    }
                }
            });
        }).catch(function () {});
    } catch (e) {
        // Silent error handling
    }
}

// YouTube video handler
/**
 * Initialize YouTube video player
 * @param {HTMLElement} container - Container element
 */
function initializeYouTubeVideo(container) {
    var youtubeIframe = container.querySelector('.video-hero-banner-youtube-video');
    if (!youtubeIframe) return;

    var autoplay = container.getAttribute('data-autoplay') === 'true';
    var els = {
        playBtn: container.querySelector('.video-hero-banner-play-btn'),
        poster: container.querySelector('.video-hero-banner-poster'),
        overlay: container.querySelector('.video-hero-banner-overlay'),
        content: container.querySelector('.video-hero-banner-content'),
        controls: container.querySelector('.video-hero-banner-controls'),
        clickOverlay: container.querySelector('.video-hero-banner-click-overlay')
    };

    // Ensure poster is visible on page load (not in autoplay mode)
    if (els.poster && !autoplay) {
        els.poster.style.cssText = 'opacity: 1; pointer-events: auto; display: block;';
    }

    if (autoplay) {
        // Hide play button, poster, overlay, and controls for autoplay
        ['playBtn', 'poster', 'overlay', 'controls'].forEach(function (key) {
            if (els[key]) els[key].style.display = 'none';
        });
        return;
    }

    /**
     * Play YouTube video by reloading iframe with autoplay parameter
     */
    function playYouTubeVideo() {
        var currentSrc = youtubeIframe.src;

        // Only add autoplay if it's not already in the URL
        if (currentSrc.indexOf('autoplay=1') === -1) {
            var separator = currentSrc.indexOf('?') > -1 ? '&' : '?';
            var newSrc = currentSrc + separator + 'autoplay=1';
            youtubeIframe.src = newSrc;
        }

        // Hide UI elements (poster, overlay, content)
        // Keep play button hidden after click
        ['poster', 'overlay', 'content'].forEach(function (key) {
            helpers.hideElement(els[key]);
        });

        // Hide play button after video starts
        if (els.playBtn) {
            helpers.hideElement(els.playBtn);
        }

        // Custom controls are already hidden via CSS for YouTube videos
        // YouTube uses its own native controls

        container.classList.add('video-playing');
    }

    // Play button click and keyboard support
    if (els.playBtn) {
        els.playBtn.addEventListener('click', playYouTubeVideo);

        // Keyboard support (Enter and Space keys)
        els.playBtn.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                playYouTubeVideo();
            }
        });
    }

    // Click overlay for play (mobile)
    if (els.clickOverlay) {
        helpers.addClickAndKeyboard(els.clickOverlay, playYouTubeVideo);
    }
}

// Router: Determine video type and delegate
/**
 * Initialize video player based on type
 * @param {HTMLElement} container - Container element
 */
function initializeVideoPlayer(container) {
    var youtubeIframe = container.querySelector('.video-hero-banner-youtube-video');
    var html5Video = container.querySelector('.video-hero-banner-html5-video');

    if (youtubeIframe) {
        initializeYouTubeVideo(container);
    } else if (html5Video) {
        initializeHTML5Video(container, html5Video);
    } else {
        initializeVimeoVideo(container);
    }
}

// Entry point: Find all containers and initialize
/**
 * Initialize all video players on the page
 */
function initializeVideoPlayers() {
    var containers = document.querySelectorAll('.video-hero-banner-container');
    if (!containers.length) return;

    // Check if Vimeo API is needed
    var needsVimeo = Array.from(containers).some(function (c) {
        return c.querySelector('.video-hero-banner-player');
    });

    // Wait for Vimeo API if needed
    if (needsVimeo && typeof window.Vimeo === 'undefined') {
        initAttempts += 1;
        if (initAttempts >= maxAttempts) {
            // If Vimeo API fails to load, initialize all non-Vimeo videos (YouTube and HTML5)
            containers.forEach(function (c) {
                var hasVimeo = c.querySelector('.video-hero-banner-player');
                if (!hasVimeo) {
                    initializeVideoPlayer(c);
                }
            });
            return;
        }
        setTimeout(initializeVideoPlayers, 100);
        return;
    }

    // Initialize all video players (each container is handled independently)
    containers.forEach(initializeVideoPlayer);
}

// Initialize on DOM ready (with defer, DOM is always ready when script executes)
if (!initStarted) {
    initStarted = true;
    initializeVideoPlayers();
}
